/**
 * كتالوج إدارة الأداء.
 *
 * دورةٌ تُفتح على معاييرَ وسُلَّم، وأهدافٌ تُكتب بمقياسها، وتقييمٌ يُرسل ثمّ
 * يُقرّه صاحبُه، وخطّةُ تحسينٍ لمن احتاجها.
 *
 * ## ولماذا المعاييرُ بياناتٌ لا كتالوج
 *
 * ما يُقاس به مندوبُ المبيعات غيرُ ما يُقاس به المحاسب، وشركةٌ تغيّر
 * معاييرَها بين سنةٍ وأخرى. فالمعاييرُ تُكتب في الدورة، ويُقترَح عليها
 * ما هو مكتوبٌ هنا بدايةً — اقتراحٌ يُحذف ويُستبدل، لا قائمةٌ مغلقة.
 *
 * ## وما لا يفعله هذا الباب
 *
 * لا يرفع أحدًا ولا يصرف مكافأة. التوصيةُ تُسجَّل في التقييم ويقرؤها من
 * يملك القرار، ثمّ يفعله في بابه — الترقيةُ تغييرُ درجةٍ في الملفّ،
 * والمكافأةُ سطرٌ في المرتّب. وربطُهما آليًّا يعني أن يرفع نصٌّ مكتوبٌ في
 * خانةٍ راتبَ رجلٍ بلا أن يقرّه أحد.
 */
import type { Labelled, StatusKind } from './common.js';

/* ==========================================================================
   حالاتُ الدورة
   ========================================================================== */

export const CYCLE_STATUSES = ['DRAFT', 'OPEN', 'CLOSED'] as const;
export type CycleStatus = (typeof CYCLE_STATUSES)[number];

export interface CycleStatusDef extends Labelled<CycleStatus> {
  description: string;
  /** هل تُكتب فيها التقييماتُ وتُعدَّل. */
  writable: boolean;
}

export const CYCLE_STATUS_DEFS: readonly CycleStatusDef[] = [
  {
    key: 'DRAFT',
    label: 'تُعدّ',
    kind: 'neutral',
    description: 'المعاييرُ والسُّلَّمُ تُحرَّر، ولا تُكتب فيها تقييمات.',
    writable: false,
  },
  {
    key: 'OPEN',
    label: 'مفتوحة',
    kind: 'info',
    description: 'تُكتب التقييماتُ وتُرسل، والمعاييرُ مجمَّدة.',
    writable: true,
  },
  {
    key: 'CLOSED',
    label: 'مغلقة',
    kind: 'success',
    description: 'انتهت — ما فيها محفوظٌ ولا يُعدَّل.',
    writable: false,
  },
] as const;

export const CYCLE_STATUS_MAP = Object.fromEntries(
  CYCLE_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CycleStatus, CycleStatusDef>>;

/* ==========================================================================
   حالاتُ التقييم
   ========================================================================== */

export const REVIEW_STATUSES = ['DRAFT', 'SUBMITTED', 'ACKNOWLEDGED'] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export interface ReviewStatusDef extends Labelled<ReviewStatus> {
  description: string;
  /** هل يُحسب في نتائج الدورة. */
  counted: boolean;
}

export const REVIEW_STATUS_DEFS: readonly ReviewStatusDef[] = [
  {
    key: 'DRAFT',
    label: 'مسوّدة',
    kind: 'neutral',
    description: 'يُكتب ولم يُرسل — لا يُحسب في شيء.',
    counted: false,
  },
  {
    key: 'SUBMITTED',
    label: 'أُرسل',
    kind: 'info',
    description: 'صار محسوبًا في نتائج الدورة، ولم يقرأه صاحبُه بعد.',
    counted: true,
  },
  {
    key: 'ACKNOWLEDGED',
    label: 'أقرّه صاحبُه',
    kind: 'success',
    description: 'قرأه ووقّع عليه — وله تعليقٌ إن علّق.',
    counted: true,
  },
] as const;

export const REVIEW_STATUS_MAP = Object.fromEntries(
  REVIEW_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ReviewStatus, ReviewStatusDef>>;

/* ==========================================================================
   حالاتُ الهدف
   ========================================================================== */

export const GOAL_STATUSES = ['OPEN', 'ACHIEVED', 'MISSED', 'DROPPED'] as const;
export type GoalStatus = (typeof GOAL_STATUSES)[number];

export interface GoalStatusDef extends Labelled<GoalStatus> {
  description: string;
  /** هل هو جارٍ بعد. */
  live: boolean;
}

export const GOAL_STATUS_DEFS: readonly GoalStatusDef[] = [
  {
    key: 'OPEN',
    label: 'جارٍ',
    kind: 'info',
    description: 'لم يُحسم بعد.',
    live: true,
  },
  {
    key: 'ACHIEVED',
    label: 'تحقّق',
    kind: 'success',
    description: 'بلغ مستهدَفه.',
    live: false,
  },
  {
    key: 'MISSED',
    label: 'لم يتحقّق',
    kind: 'danger',
    description: 'انتهت مدّتُه ولم يبلغ مستهدَفه.',
    live: false,
  },
  {
    key: 'DROPPED',
    label: 'أُسقط',
    kind: 'neutral',
    description: 'تغيّرت الأولويّاتُ فلم يعد مطلوبًا — ولا يُحسب عليه.',
    live: false,
  },
] as const;

export const GOAL_STATUS_MAP = Object.fromEntries(
  GOAL_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<GoalStatus, GoalStatusDef>>;

/* ==========================================================================
   حالاتُ خطّة التحسين
   ========================================================================== */

export const PLAN_STATUSES = ['OPEN', 'MET', 'FAILED', 'CANCELLED'] as const;
export type PlanStatus = (typeof PLAN_STATUSES)[number];

export interface PlanStatusDef extends Labelled<PlanStatus> {
  description: string;
  live: boolean;
}

export const PLAN_STATUS_DEFS: readonly PlanStatusDef[] = [
  {
    key: 'OPEN',
    label: 'جارية',
    kind: 'warning',
    description: 'في مدّتها، وتُراجَع عند نهايتها.',
    live: true,
  },
  {
    key: 'MET',
    label: 'تحقّقت',
    kind: 'success',
    description: 'بلغ ما اتُّفق عليه — وتُغلق الخطّة.',
    live: false,
  },
  {
    key: 'FAILED',
    label: 'لم تتحقّق',
    kind: 'danger',
    description: 'انتهت المدّةُ ولم يبلغ — وما بعدها قرارُ الإدارة.',
    live: false,
  },
  {
    key: 'CANCELLED',
    label: 'أُلغيت',
    kind: 'neutral',
    description: 'تغيّر الظرفُ فلم تعد قائمةً — ولا تُقرأ حكمًا عليه.',
    live: false,
  },
] as const;

export const PLAN_STATUS_MAP = Object.fromEntries(
  PLAN_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<PlanStatus, PlanStatusDef>>;

/* ==========================================================================
   التوصيات
   ========================================================================== */

export const PERF_RECOMMENDATIONS = [
  'PROMOTE',
  'RAISE',
  'KEEP',
  'DEVELOP',
  'PLAN',
] as const;
export type PerfRecommendation = (typeof PERF_RECOMMENDATIONS)[number];

export interface PerfRecommendationDef extends Labelled<PerfRecommendation> {
  description: string;
  /** الشاشةُ التي يُنفَّذ فيها ما أُوصي به — لا يُنفَّذ من التقييم. */
  actionPath: string | null;
  actionLabel: string | null;
}

export const PERF_RECOMMENDATION_DEFS: readonly PerfRecommendationDef[] = [
  {
    key: 'PROMOTE',
    label: 'يُرقَّى',
    kind: 'success',
    description: 'يستحقّ وظيفةً أعلى أو درجةً أعلى.',
    actionPath: '/hr/employees',
    actionLabel: 'غيّر وظيفتَه ودرجتَه في ملفّه',
  },
  {
    key: 'RAISE',
    label: 'علاوة',
    kind: 'success',
    description: 'يبقى في وظيفته ويستحقّ زيادةً في أجره.',
    actionPath: '/hr/employees',
    actionLabel: 'عدّل أجرَه الأساسيّ في ملفّه',
  },
  {
    key: 'KEEP',
    label: 'يبقى كما هو',
    kind: 'neutral',
    description: 'أداءٌ مستقرٌّ لا يستدعي تغييرًا.',
    actionPath: null,
    actionLabel: null,
  },
  {
    key: 'DEVELOP',
    label: 'يحتاج تدريبًا',
    kind: 'info',
    description: 'فجوةٌ تُسدّ بتدريبٍ لا بإجراء.',
    actionPath: '/hr/learning',
    actionLabel: 'اكتب له احتياجًا تدريبيًّا',
  },
  {
    key: 'PLAN',
    label: 'خطّة تحسين',
    kind: 'danger',
    description: 'أداءٌ دون المطلوب — تُفتح له خطّةٌ بمدّةٍ وأهداف.',
    actionPath: '/hr/performance',
    actionLabel: 'افتح له خطّةَ تحسين',
  },
] as const;

export const PERF_RECOMMENDATION_MAP = Object.fromEntries(
  PERF_RECOMMENDATION_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<PerfRecommendation, PerfRecommendationDef>>;

/* ==========================================================================
   المعايير
   ========================================================================== */

/**
 * معيارٌ في دورة.
 *
 * المفتاحُ ثابتٌ لا يتغيّر: درجاتُ التقييمات مخزَّنةٌ بمفاتيحها، وتغييرُ
 * مفتاحٍ بعد أن كُتبت عليه درجاتٌ يترك أرقامًا لا يُعرف ما تقيس.
 */
export interface Criterion {
  key: string;
  label: string;
  /** وزنُه في المحصّلة — مئويّ. */
  weight: number;
  description?: string;
}

export const MAX_CRITERIA = 20;
export const CRITERION_KEY_PATTERN = /^[A-Z][A-Z0-9_]{1,29}$/;

/** أعلى ما يُقبل في السُّلَّم، وأقلُّه: سُلَّمٌ من درجتين لا يميّز أحدًا. */
export const MIN_SCALE = 3;
export const MAX_SCALE = 10;

/**
 * معاييرُ تُقترَح على دورةٍ جديدة.
 *
 * تُكتب في الشاشة زرًّا يملأ الجدول، ثمّ تُحذف وتُستبدل وتُعاد أوزانُها.
 * وبديلُها صفحةٌ فارغةٌ يقف أمامها من لم يكتب معاييرَ أداءٍ قطّ.
 */
export const SUGGESTED_CRITERIA: readonly Criterion[] = [
  {
    key: 'QUALITY',
    label: 'جودة العمل',
    weight: 25,
    description: 'دقّةُ ما يُسلّمه وقلّةُ ما يُعاد منه.',
  },
  {
    key: 'DELIVERY',
    label: 'الالتزام بالمواعيد',
    weight: 20,
    description: 'يُسلّم في وقته، ويُنذر مبكّرًا إن تأخّر.',
  },
  {
    key: 'OWNERSHIP',
    label: 'تحمّل المسؤولية',
    weight: 20,
    description: 'يُنهي ما بدأه ولا ينتظر أن يُذكَّر.',
  },
  {
    key: 'TEAMWORK',
    label: 'العمل مع الفريق',
    weight: 20,
    description: 'يُعين ويُعان، ويحلّ الخلافَ في موضعه.',
  },
  {
    key: 'GROWTH',
    label: 'التطوّر',
    weight: 15,
    description: 'يتعلّم ممّا يُلاحَظ عليه ولا يكرّره.',
  },
] as const;

/**
 * قراءةُ معايير دورةٍ من عمود JSON.
 *
 * ما يُقرأ من قاعدةٍ لا يُوثق به: عمودُ JSON يقبل ما كُتب فيه من إصدارٍ
 * أقدمَ أو من إدخالٍ يدويّ. والمفتاحُ المكرّرُ يُسقَط — وإلّا صارت درجةٌ
 * واحدةٌ تُقرأ لمعيارين.
 */
export function readCriteria(raw: unknown): Criterion[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: Criterion[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const key = typeof row.key === 'string' ? row.key : '';
    const label = typeof row.label === 'string' ? row.label.trim() : '';
    if (!CRITERION_KEY_PATTERN.test(key) || !label) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      key,
      label,
      weight:
        typeof row.weight === 'number' && Number.isFinite(row.weight)
          ? Math.max(0, Math.min(100, Math.round(row.weight)))
          : 0,
      ...(typeof row.description === 'string' && row.description
        ? { description: row.description }
        : {}),
    });
    if (out.length >= MAX_CRITERIA) break;
  }
  return out;
}

/**
 * المحصّلةُ الموزونة.
 *
 * تُحسب ولا تُكتب: رقمٌ يُكتب يدًا يخالف الدرجاتِ فوقه، ويُقرأ على أنّه
 * خلاصتُها. ومعيارٌ بلا درجةٍ يخرج من البسط والمقام معًا — تقييمٌ نصفُه
 * مكتوبٌ يُقرأ على نصفه لا على الكلّ.
 *
 * وبأوزانٍ كلُّها أصفار يُؤخذ المتوسّطُ البسيط: الوزنُ اختياريّ، وشركةٌ
 * لا تزن معاييرَها تريد المتوسّط لا صفرًا.
 */
export function weightedScore(
  criteria: readonly Criterion[],
  scores: Record<string, unknown>,
): number | null {
  let sum = 0;
  let weights = 0;
  let count = 0;
  let plain = 0;

  for (const c of criteria) {
    const raw = scores[c.key];
    const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
    if (value === null) continue;
    count++;
    plain += value;
    sum += value * c.weight;
    weights += c.weight;
  }

  if (count === 0) return null;
  const value = weights > 0 ? sum / weights : plain / count;
  return Math.round(value * 100) / 100;
}

/**
 * ما يُقرأ من درجاتِ تقييم.
 *
 * المفاتيحُ التي لا معيارَ لها تُسقَط: معيارٌ حُذف من الدورة قبل فتحها
 * يترك درجتَه في الصفّ، وحسابُها في المحصّلة يجعل المجموعَ لا يُفسَّر.
 */
export function readScores(
  raw: unknown,
  criteria: readonly Criterion[],
  scaleMax: number,
): Record<string, number> {
  if (!raw || typeof raw !== 'object') return {};
  const row = raw as Record<string, unknown>;
  const out: Record<string, number> = {};
  for (const c of criteria) {
    const value = row[c.key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    if (value < 1 || value > scaleMax) continue;
    out[c.key] = Math.round(value * 100) / 100;
  }
  return out;
}

/* ==========================================================================
   مراتبُ الأداء
   ========================================================================== */

export const PERFORMANCE_BANDS = ['TOP', 'STRONG', 'MEETS', 'BELOW'] as const;
export type PerformanceBand = (typeof PERFORMANCE_BANDS)[number];

export interface BandDef extends Labelled<PerformanceBand> {
  description: string;
  /** أقلُّ نسبةٍ من السُّلَّم تُدخل هذه المرتبة. */
  minPercent: number;
}

/**
 * المراتبُ تُقاس بالنسبة لا بالدرجة.
 *
 * سُلَّمُ الخمسةِ غيرُ سُلَّم العشرة، و«أربعة» فيهما مرتبتان مختلفتان.
 * فالنسبةُ من أعلى السُّلَّم هي ما يُقارَن، وهي ما يجعل دورتين بسُلَّمين
 * مختلفين تُقرآن معًا.
 */
export const BAND_DEFS: readonly BandDef[] = [
  {
    key: 'TOP',
    label: 'متميّز',
    kind: 'success',
    description: 'فوق المتوقَّع بوضوح — وهؤلاء من يُحتفظ بهم.',
    minPercent: 90,
  },
  {
    key: 'STRONG',
    label: 'فوق المتوقَّع',
    kind: 'info',
    description: 'أداءٌ قويٌّ مستقرّ.',
    minPercent: 75,
  },
  {
    key: 'MEETS',
    label: 'يفي بالمطلوب',
    kind: 'neutral',
    description: 'يؤدّي ما عليه.',
    minPercent: 55,
  },
  {
    key: 'BELOW',
    label: 'دون المطلوب',
    kind: 'danger',
    description: 'فجوةٌ تُعالَج بخطّةٍ مكتوبةٍ بمدّةٍ وأهداف.',
    minPercent: 0,
  },
] as const;

export const BAND_MAP = Object.fromEntries(
  BAND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<PerformanceBand, BandDef>>;

/** مرتبةُ محصّلةٍ على سُلَّمها — فارغةٌ لمن لا محصّلةَ له. */
export function bandOf(
  overall: number | null,
  scaleMax: number,
): PerformanceBand | null {
  if (overall === null || scaleMax <= 0) return null;
  const percent = (overall / scaleMax) * 100;
  for (const d of BAND_DEFS) {
    if (percent >= d.minPercent) return d.key;
  }
  return 'BELOW';
}

/* ==========================================================================
   أهدافُ الخطّة
   ========================================================================== */

/** بندٌ في خطّة تحسين: نصٌّ وحالة. */
export interface PlanObjective {
  text: string;
  done: boolean;
}

export const MAX_PLAN_OBJECTIVES = 12;

export function readObjectives(raw: unknown): PlanObjective[] {
  if (!Array.isArray(raw)) return [];
  const out: PlanObjective[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const text = typeof row.text === 'string' ? row.text.trim() : '';
    if (!text) continue;
    out.push({ text, done: row.done === true });
    if (out.length >= MAX_PLAN_OBJECTIVES) break;
  }
  return out;
}

/* ==========================================================================
   عتباتُ الإحصاء
   ========================================================================== */

/**
 * أقلُّ عددٍ يُنشر عنه متوسّطُ أداء.
 *
 * متوسّطُ ثلاثةِ تقييماتٍ في إدارةٍ يكشف أصحابَه لمن يعرف عددَهم، ويُقرأ
 * على أنّه حكمٌ على الإدارة. والعتبةُ تحمي الاثنين: الخصوصيّةَ والمعنى.
 */
export const MIN_SAMPLE_FOR_AVERAGE = 5;

/** لِمَ لم يُعرض رقمٌ — يُقال ولا يُترك الفراغُ يُفسَّر. */
export interface StatGap {
  reason: string;
}
